import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { anonymousAPI, trackingAPI } from '../services/api';
import { getLocalizedPath } from '../i18n';
import { UploadIcon, AlertCircleIcon, RefreshCwIcon, HomeIcon, UserIcon } from './Icons';

interface AnonymousUploadProps {
  onSuccess?: () => void;
  /** If provided, skip role modal and use this role directly */
  preSelectedRole?: 'tenant' | 'landlord' | null;
  /** Callback when role modal should be shown (for external trigger flow) */
  onRequestRoleSelection?: () => void;
}

export interface AnonymousUploadRef {
  triggerFileSelect: () => void;
  uploadWithRole: (file: File, role: 'tenant' | 'landlord') => Promise<void>;
}

const AnonymousUpload = forwardRef<AnonymousUploadRef, AnonymousUploadProps>(({
  onSuccess,
  preSelectedRole,
  onRequestRoleSelection
}, ref) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['landing', 'common']);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'tenant' | 'landlord' | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getTrackingSession = () => localStorage.getItem('tracking_session_id') || undefined;
  const getCampaignSource = () => localStorage.getItem('campaign_source') || undefined;

  const trackButtonClick = () => {
    trackingAPI.logCtaClick({
      cta_id: 'select_contract',
      page: 'landing',
      location: 'select_contract',
      session_id: getTrackingSession(),
      language: i18n.language,
    });
  };

  const trackFunnelStep = (step: 'started' | 'role_selected' | 'file_selected' | 'upload_completed', role?: 'tenant' | 'landlord', buttonLocation?: string) => {
    trackingAPI.trackUploadFunnel({
      step,
      session_id: getTrackingSession(),
      campaign_source: getCampaignSource(),
      role,
      button_location: buttonLocation,
    }).catch(() => { /* ignore tracking errors */ });
  };

  // Expose methods for external control
  useImperativeHandle(ref, () => ({
    triggerFileSelect: () => {
      fileInputRef.current?.click();
    },
    uploadWithRole: async (file: File, role: 'tenant' | 'landlord') => {
      await performUpload(file, role);
    }
  }));

  const validateFile = (file: File): string | null => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return t('anonymousUpload.errorType');
    }

    // Validate file size (15MB)
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      return t('anonymousUpload.errorSize');
    }

    return null;
  };

  const performUpload = async (file: File, role: 'tenant' | 'landlord') => {
    setUploading(true);
    setError(null);

    try {
      const response = await anonymousAPI.upload(file, role, i18n.language);
      const { session_id } = response.data;

      // Track successful upload
      trackFunnelStep('upload_completed', role);

      // Redirect to preview page with language prefix
      const previewPath = getLocalizedPath(`/preview/${session_id}`, i18n.language);
      navigate(previewPath);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || t('anonymousUpload.errorGeneric');
      setError(errorMessage);
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Use pre-selected role from props, or internally selected role
    const roleToUse = preSelectedRole || selectedRole;

    // Track file selection
    trackFunnelStep('file_selected', roleToUse || undefined);

    if (roleToUse) {
      performUpload(file, roleToUse);
      // Reset selected role after upload
      setSelectedRole(null);
    } else {
      // This shouldn't happen in normal flow since role modal is shown first
      // But just in case, show role modal
      setShowRoleModal(true);
    }
  };

  const handleRoleSelection = async (role: 'tenant' | 'landlord') => {
    if (!selectedFile) return;

    setShowRoleModal(false);
    await performUpload(selectedFile, role);
  };

  const handleUploadBoxClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackButtonClick();

    // If role is pre-selected, go directly to file selection
    if (preSelectedRole) {
      fileInputRef.current?.click();
    } else {
      // Track funnel start and show role modal
      trackFunnelStep('started', undefined, 'upload_box');
      setShowRoleModal(true);
    }
  };

  const handleRoleAndTriggerFile = (role: 'tenant' | 'landlord') => {
    // Track role selection
    trackFunnelStep('role_selected', role);

    setSelectedRole(role);
    setShowRoleModal(false);
    // Trigger file selection after role is selected
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  return (
    <div className="anonymous-upload">
      <div className="upload-box">
        {!uploading ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              id="anonymous-file-upload"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div className="upload-label" onClick={handleUploadBoxClick} role="button" tabIndex={0}>
              <UploadIcon className="h-12 w-12 upload-icon mb-4" />
              <p className="text-lg font-semibold text-gray-900 mb-2">
                {t('anonymousUpload.title')}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                {t('anonymousUpload.subtitle')}
              </p>
              <div className="upload-cta-button">
                {t('anonymousUpload.cta')}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {t('anonymousUpload.hint')}
              </p>
              <p className="upload-privacy-note">
                {t('anonymousUpload.privacyNote')}
              </p>
            </div>
          </>
        ) : (
          <div className="upload-progress text-center">
            <RefreshCwIcon className="h-12 w-12 text-green-600 animate-spin mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-900">
              {t('common:buttons.loading', { defaultValue: 'Uploading...' })}
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <AlertCircleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Role selection modal */}
      {showRoleModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">{t('anonymousUpload.roleTitle')}</h2>
            <p className="modal-subtitle">
              {t('anonymousUpload.roleSubtitle')}
            </p>
            <div className="role-buttons">
              <button
                className="role-button tenant-button"
                onClick={() => selectedFile ? handleRoleSelection('tenant') : handleRoleAndTriggerFile('tenant')}
              >
                <div className="role-icon"><UserIcon size={32} /></div>
                <div className="role-title">{t('anonymousUpload.tenantTitle')}</div>
                <div className="role-description">{t('anonymousUpload.tenantDesc')}</div>
              </button>
              <button
                className="role-button landlord-button"
                onClick={() => selectedFile ? handleRoleSelection('landlord') : handleRoleAndTriggerFile('landlord')}
              >
                <div className="role-icon"><HomeIcon size={32} /></div>
                <div className="role-title">{t('anonymousUpload.landlordTitle')}</div>
                <div className="role-description">{t('anonymousUpload.landlordDesc')}</div>
              </button>
            </div>
            <button
              className="modal-close"
              onClick={() => {
                setShowRoleModal(false);
                setSelectedFile(null);
                setSelectedRole(null);
              }}
            >
              {t('common:buttons.cancel')}
            </button>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .upload-box {
          background: white;
          border: 2px dashed #d1d5db;
          border-radius: 1rem;
          padding: 3rem 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .upload-box:hover {
          border-color: #0c8b5c;
          background: #f4fbf7;
        }

        .upload-label {
          cursor: pointer;
          display: block;
        }

        .upload-icon {
          color: #0c8b5c;
        }

        .upload-cta-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #0c8b5c;
          color: #ffffff;
          padding: 0.9rem 1.6rem;
          border-radius: 14px;
          font-weight: 700;
          font-size: 0.95rem;
          box-shadow: 0 12px 28px rgba(12, 139, 92, 0.22);
          transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .upload-cta-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 16px 34px rgba(12, 139, 92, 0.26);
          background: #0a7a4a;
        }

        .upload-cta-button:active {
          transform: translateY(0);
          box-shadow: 0 10px 22px rgba(12, 139, 92, 0.2);
          background: #096b42;
        }

        .upload-privacy-note {
          margin-top: 6px;
          font-size: 0.8rem;
          color: #6b7280;
        }

        .upload-progress {
          padding: 2rem;
        }
      `}</style>
    </div>
  );
});

AnonymousUpload.displayName = 'AnonymousUpload';

export default AnonymousUpload;
